import { Menu } from '@headlessui/react';
import clsx from 'clsx';
import { useExperimentFlags } from '@hungryroot/experiments';
import { Price, Tooltip } from '@hungryroot/pho';
import {
  useGetCustomerRewardCredits,
  useIdentify,
} from '@hungryroot/rest-hooks';
import {
  ACCOUNT_REFERRAL_ROUTE,
  PLAN_ROUTE,
  REWARDS_ROUTE,
} from '@hungryroot/routes';
import { Heading, Text, CaretDown } from '@hungryroot/ui-kit';
import styles from './AccountDropdown.module.css';

const REWARDS_ICON_SRC = '/public/img/exp-245/rewards-badge.png';

interface Props {
  testid?: string;
  accountBalance?: number;
  showTooltip: boolean;
  tooltipTrackingProp?: string;
}

function setLocalStorage(trackingProp: string) {
  return window.localStorage.setItem(trackingProp, Date());
}

export function AccountDropdown(props: Props) {
  const { testid, accountBalance, showTooltip, tooltipTrackingProp } = props;

  return (
    <Menu as="div" className={styles['menu-wrapper']}>
      <Menu.Button
        className={styles['menu-button']}
        data-testid={`${testid}-trigger`}
        data-trigger="tooltip-dismiss"
      >
        <Heading variant="H6" className={styles['menu-trigger-text']}>
          Account
        </Heading>
        <CaretDown />
        {showTooltip && accountBalance ? (
          <Tooltip
            testid={`${testid}-tooltip`}
            className={styles['wallet-tooltip']}
            content={`You have $${accountBalance.toFixed(2)} in your account`}
            maxWidth=""
            placement="bottom end"
            trigger="manual"
            showOnCreate={true}
            hideOnClick={false}
            dismissClickTarget="[data-trigger=tooltip-dismiss]"
            scrollDismiss={false}
            dismissDelay={10000}
            onShown={
              tooltipTrackingProp
                ? () => {
                    setLocalStorage(tooltipTrackingProp);
                  }
                : undefined
            }
          >
            <span className={styles['wallet-tooltip-anchor']}></span>
          </Tooltip>
        ) : null}
      </Menu.Button>
      <Menu.Items
        className={styles['menu-dropdown']}
        data-testid={`${testid}-menu`}
      >
        <PreCheckoutMenuItems testid={testid} />
        <PostCheckMenuItems testid={testid} accountBalance={accountBalance} />
        <hr className={styles['hr-line']} />
        <Menu.Item>
          {({ active }) => (
            <a
              className={active ? styles['link-hover'] : styles['link']}
              href="/logout/"
              data-testid={`${testid}-link-logout`}
            >
              <Text className={styles['menu-button-text']}>Logout</Text>
            </a>
          )}
        </Menu.Item>
      </Menu.Items>
    </Menu>
  );
}

function PreCheckoutMenuItems({ testid }: { testid: string | undefined }) {
  const { customerData } = useIdentify();
  const didCheckout = customerData?.did_checkout;

  if (didCheckout) {
    return null;
  }

  return (
    <>
      <Menu.Item>
        {({ active }) => (
          <a
            className={active ? styles['link-hover'] : styles['link']}
            href="/quiz/"
            data-testid={`${testid}-quiz`}
          >
            <Text className={styles['menu-button-text']}>Get Started</Text>
          </a>
        )}
      </Menu.Item>
    </>
  );
}

function PostCheckMenuItems({
  testid,
  accountBalance,
}: {
  testid: string | undefined;
  accountBalance: number | undefined;
}) {
  const { customerData } = useIdentify();
  const didCheckout = customerData?.did_checkout;
  const { isLoading: isExperimentsLoading, sprinklesExp245 } =
    useExperimentFlags(245);
  const isRewardsVariant =
    !isExperimentsLoading && sprinklesExp245?.isVariant === true;
  const { data: rewardCredits } = useGetCustomerRewardCredits({
    enabled: isRewardsVariant,
  });

  if (didCheckout === undefined || didCheckout === false) {
    return null;
  }

  return (
    <>
      <Menu.Item>
        {({ active }) => (
          <a
            className={active ? styles['link-hover'] : styles['link']}
            href={PLAN_ROUTE}
            data-testid={`${testid}-plan`}
          >
            <Text className={styles['menu-button-text']}>Your Plan</Text>
          </a>
        )}
      </Menu.Item>
      <Menu.Item>
        {({ active }) => (
          <a
            className={active ? styles['link-hover'] : styles['link']}
            href="/account/settings/"
            data-testid={`${testid}-link-settings`}
          >
            <Text className={styles['menu-button-text']}>Settings</Text>
          </a>
        )}
      </Menu.Item>
      <Menu.Item>
        {({ active }) => (
          <a
            className={active ? styles['link-hover'] : styles['link']}
            href="/account/history/"
            data-testid={`${testid}-link-order-history`}
          >
            <Text className={styles['menu-button-text']}>Order History</Text>
          </a>
        )}
      </Menu.Item>
      <Menu.Item>
        {({ active }) => (
          <a
            className={active ? styles['link-hover'] : styles['link']}
            href="/faq/"
            target="_blank"
            data-testid={`${testid}-link-help-center`}
          >
            <Text className={styles['menu-button-text']}>Help Center</Text>
          </a>
        )}
      </Menu.Item>
      <Menu.Item>
        {({ active }) => (
          <a
            className={active ? styles['link-hover-flex'] : styles['link-flex']}
            href="/wallet/"
            data-testid={`${testid}-link-wallet`}
          >
            <Text data-testid="testing" className={styles['menu-button-text']}>
              Wallet
            </Text>
            {accountBalance ? (
              <Text
                testid={`${testid}-account-balance`}
                className={styles['menu-button-text-callout']}
              >
                <Price amount={accountBalance} />
              </Text>
            ) : null}
          </a>
        )}
      </Menu.Item>
      {isRewardsVariant ? (
        <Menu.Item>
          {({ active }) => (
            <a
              className={clsx({
                [styles['link-hover-flex']]: active,
                [styles['link-flex']]: !active,
              })}
              href={REWARDS_ROUTE}
              data-testid={`${testid}-link-rewards`}
            >
              <span className={styles['rewards-label-group']}>
                <img
                  src={REWARDS_ICON_SRC}
                  alt=""
                  aria-hidden
                  className={styles['rewards-icon']}
                />
                <Text className={styles['menu-button-text']}>Rewards</Text>
              </span>
              {rewardCredits?.reward_credits ? (
                <Text
                  testid={`${testid}-rewards-credits`}
                  className={styles['menu-button-text-callout']}
                >
                  {rewardCredits.reward_credits} credits
                </Text>
              ) : null}
            </a>
          )}
        </Menu.Item>
      ) : null}
      <Menu.Item>
        {({ active }) => (
          <a
            className={active ? styles['link-hover'] : styles['link']}
            href={ACCOUNT_REFERRAL_ROUTE}
            data-testid={`${testid}-link-give`}
          >
            <Text className={styles['menu-button-text']}>Refer a Friend</Text>
          </a>
        )}
      </Menu.Item>
      <Menu.Item>
        {({ active }) => (
          <a
            className={active ? styles['link-hover'] : styles['link']}
            href="/gift/"
            data-testid={`${testid}-link-gifts`}
          >
            <Text className={styles['menu-button-text']}>Gifts</Text>
          </a>
        )}
      </Menu.Item>
      <Menu.Item>
        {({ active }) => (
          <a
            className={active ? styles['link-hover'] : styles['link']}
            href="https://www.facebook.com/groups/hungryroot"
            target="_blank"
            data-testid={`${testid}-link-community`}
          >
            <Text className={styles['menu-button-text']}>Community</Text>
          </a>
        )}
      </Menu.Item>
    </>
  );
}
